import { useMutation, useQuery } from "@tanstack/react-query";
import adminService from "../services/admin";
import { FETCH_QUERY_KEYS } from "../consts";
import { CACHE_DURATION } from "@consts";

export const useFetchOpenDrafts = () => useQuery({
  queryKey: [FETCH_QUERY_KEYS.OPEN_DRAFTS],
  queryFn: adminService.fetchOpenDrafts,
  staleTime: CACHE_DURATION.ONE_MINUTE,
  select: (data) => data?.map((draft, index) => ({ ...draft, id: `draft-${index}` }))
});

export const useDuplicateAssistant = () => useMutation({
  mutationFn: adminService.duplicateAssistant
});

export const useExtendDraft = (options = {}) => useMutation({
  mutationFn: adminService.extendDraft,
  ...options
});

export const useChangeDraftOwner = (options = {}) => useMutation({
  mutationFn: adminService.changeDraftOwner,
  ...options
});

export const useFetchDraftOwners = ({ enabled, accountId }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DRAFT_OWNERS, accountId],
  queryFn: () => adminService.fetchDraftOwners(accountId),
  enabled
});

export const useFetchFailures = () => useQuery({
  queryKey: [FETCH_QUERY_KEYS.FAILURES],
  queryFn: adminService.fetchFailures,
  staleTime: CACHE_DURATION.ONE_MINUTE,
  select: (data) => data?.map((failure, index) => ({ ...failure, id: `failure-${index}` }))
});

export const useFetchDataMappingsTables = (options = {}) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DATA_MAPPINGS_TABLES],
  queryFn: adminService.fetchDataMappingsTables,
  ...options
});

export const useFetchSpecRefs = (collectionName, channel) => useQuery({
  queryKey: ["specRefs", collectionName, channel],
  queryFn: () => adminService.fetchSpecRefs({ collectionName, channel }),
  enabled: !!collectionName && !!channel
});

export const useFetchTablesOptions = (collectionName, collectionInstanceId) => useQuery({
  queryKey: ["tablesOptions", collectionName, collectionInstanceId],
  queryFn: () => adminService.fetchTablesOptions({ collectionName, collectionInstanceId }),
  enabled: !!collectionName && !!collectionInstanceId
});

export const useFetchTableByTableId = (params) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.DATA_MAPPING_TABLE, params?.tableId, params?.collectionName, params?.collectionInstanceId],
  queryFn: () => adminService.fetchTableByTableId(params),
  enabled: !!params?.tableId && !!params?.collectionName && !!params?.collectionInstanceId,
  refetchOnWindowFocus: false
});

export const useUpdateTableFields = () => useMutation({
  mutationFn: adminService.updateTableFields
});

export const useCreateTable = () => useMutation({
  mutationFn: adminService.createTable
});

export const useUpdateGrouping = () => useMutation({
  mutationFn: adminService.updateGrouping
});

export const useFetchSftpSettings = ({ accountId, enabled }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.SFTP_SETTINGS, accountId],
  queryFn: () => adminService.fetchSftpSettings(accountId),
  enabled,
  gcTime: 0
});

export const useFetchConfigurableContent = (collectionName, collectionSpecKey) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.CONFIGURABLE_CONTENT, collectionName, collectionSpecKey],
  queryFn: () => adminService.fetchConfigurableContent({ collectionName, collectionSpecKey }),
  enabled: !!collectionName && !!collectionSpecKey,
  refetchOnWindowFocus: false
});

export const useBulkUpsertConfigurableContent = () => useMutation({
  mutationFn: adminService.bulkUpsertConfigurableContent
});

export const useDeleteConfigurableContent = () => useMutation({
  mutationFn: adminService.deleteConfigurableContent
});

export const useGetConfigurableContentDefaults = () => useMutation({
  mutationFn: adminService.getConfigurableContentDefaults
});

export const useInitializeConfigurableContent = () => useMutation({
  mutationFn: adminService.initializeConfigurableContent
});

export const useFetchAdminSubscription = ({ accountId, enabled }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.SUBSCRIPTION, accountId],
  queryFn: () => adminService.fetchSubscription(accountId),
  enabled
});

export const useFetchFeatureAdoption = () => useQuery({
  queryKey: [FETCH_QUERY_KEYS.FEATURE_ADOPTION],
  queryFn: adminService.fetchFeatureAdoption
});

export const useFetchFeatureAgents = ({ featureId, params, enabled }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.FEATURE_AGENTS, featureId, params],
  queryFn: () => adminService.fetchFeatureAgents({ featureId, ...params }),
  enabled: !!featureId && enabled,
  placeholderData: (previousData) => previousData
});

export const useFetchAgentFeatures = ({ assistantId, params, enabled }) => useQuery({
  queryKey: [FETCH_QUERY_KEYS.AGENT_FEATURES, assistantId, params],
  queryFn: () => adminService.fetchAgentFeatures({ assistantId, ...params }),
  enabled: !!assistantId && enabled,
  placeholderData: (previousData) => previousData
});

